import React, { useState, useEffect } from "react";
import BookingLayout from "../../Components/PublicLayout";
import PatientForm from "./PatientForm";
import BookingCalendar from "./BookingCalendar";
import { bookingRvService } from "../../services/bookingRvService";
import { Doctor, CreateBookingRequest } from "../../types/BookingRv";
import { ErrorSuccessModal } from "../../Modals/ErrorSuccessModal";

const getTodayDateString = () => {
  const today = new Date();
  return today.toISOString().split("T")[0];
};

function BookingPage() {
  const [surname, setSurname] = useState("");
  const [amka, setAmka] = useState("");
  const [email, setEmail] = useState("");
  const [rvReason, setRvReason] = useState("");
  const [doctorId, setDoctorId] = useState("");
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [loadingDoctors, setLoadingDoctors] = useState(true);

  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState(getTodayDateString());

  // --- States για το δικό σου ErrorSuccessModal ---
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState<"success" | "error">("success");

  // 1. Φόρτωση γιατρών
  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const data = await bookingRvService.getDoctors();
        setDoctors(data);
      } catch (error) {
        console.error("Σφάλμα κατά τη φόρτωση των γιατρών:", error);
      } finally {
        setLoadingDoctors(false);
      }
    };

    fetchDoctors();
  }, []);

  // 2. Φόρτωση slots
  useEffect(() => {
    if (!doctorId) {
      setAvailableSlots([]);
      setSelectedSlot(null);
      return;
    }

    const fetchSlots = async () => {
      setLoadingSlots(true);
      setSelectedSlot(null);
      try {
        const slots = await bookingRvService.getAvailableSlots(
          doctorId,
          appointmentDate,
        );
        setAvailableSlots(slots);
      } catch (error) {
        console.error("Σφάλμα slots:", error);
        setAvailableSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };

    fetchSlots();
  }, [doctorId, appointmentDate]);

  // 3. Υποβολή ραντεβού
  const handleBookingSubmit = async (formData: {
    surname: string;
    amka: string;
    email: string;
    rvReason: string;
    doctorId: string;
    appointmentDate: string;
    selectedSlot: string | null;
  }) => {
    if (!formData.selectedSlot) {
      setModalTitle("Προσοχή");
      setModalMessage("Παρακαλώ επιλέξτε ώρα.");
      setModalType("error");
      setModalOpen(true);
      return;
    }

    const requestData: CreateBookingRequest = {
      surname: formData.surname,
      amka: formData.amka,
      doctorId: formData.doctorId,
      email: formData.email || "",
      rvReason: formData.rvReason || "",
      appointmentDate: formData.appointmentDate,
      selectedSlot: formData.selectedSlot,
    };

    try {
      const result = await bookingRvService.createBooking(requestData);

      // Επιτυχία
      setModalTitle("Επιτυχία");
      setModalMessage(
        `Το ραντεβού για τις ${appointmentDate} καταχωρήθηκε επιτυχώς!`,
      );
      setModalType("success");
      setModalOpen(true);
      console.log("Created:", result);
    } catch (error: any) {
      // Αποτυχία (παίρνει το μήνυμα από το backend)
      setModalTitle("Σφάλμα");
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Σφάλμα κατά την κράτηση.";
      setModalMessage(errorMessage);
      setModalType("error");
      setModalOpen(true);
    }
  };

  return (
    <>
      <BookingLayout
        leftContent={
          <PatientForm
            surname={surname}
            setSurname={setSurname}
            amka={amka}
            setAmka={setAmka}
            email={email}
            setEmail={setEmail}
            rvReason={rvReason}
            setRvReason={setRvReason}
            doctorId={doctorId}
            setDoctorId={setDoctorId}
            appointmentDate={appointmentDate}
            setAppointmentDate={setAppointmentDate}
            doctors={doctors}
            loadingDoctors={loadingDoctors}
            selectedSlot={selectedSlot}
            onSubmit={handleBookingSubmit}
          />
        }
        rightContent={
          <BookingCalendar
            availableSlots={availableSlots}
            loadingSlots={loadingSlots}
            selectedSlot={selectedSlot}
            setSelectedSlot={setSelectedSlot}
            isDoctorSelected={!!doctorId}
          />
        }
      />

      {/* Χρησιμοποιούμε το ErrorSuccessModal σου */}
      <ErrorSuccessModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        message={modalMessage}
        type={modalType}
      />
    </>
  );
}

export default BookingPage;
